import classnames from "classnames";
import { CalendarDays, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { useLocale, useTranslation } from "@/lib/i18n";

import { riskPictureCellLabel, riskPicturePeriodLabel } from "./cell-label";
import { riskPictureBadgeUrl } from "./risk-picture-badge";
import { riskPictureDayTitle } from "./risk-picture-day-title";
import {
  riskPictureAreaDescription,
  type RiskPictureDay,
  type RiskPictureItem,
} from "./risk-picture-model";
import {
  riskPicturePhenomenonIconUrl,
  riskPicturePhenomenonName,
} from "./risk-picture-phenomena";
import {
  riskPictureSidebarView,
  type RiskPictureSelection,
} from "./risk-picture-selection";
import styles from "./risk-picture.module.css";

interface RiskPictureSidebarProps {
  days: RiskPictureDay[];
  dayIndex: number;
  /** Details are shown whenever set, on top of the calendar, whether it is open or not. */
  selectedItem?: RiskPictureItem;
  /** The calendar (day list). Independent of `selectedItem`; only its own toggle and X change it. */
  calendarOpen: boolean;
  onCalendarOpenChange: (open: boolean) => void;
  onSelectDay: (index: number) => void;
  onSelectItem: (dayIndex: number, itemId: string) => void;
  onHoverItem: (item: RiskPictureSelection | undefined) => void;
  /** Warning hovered anywhere. Only passed while open; its day-list row is highlighted. */
  highlighted?: RiskPictureSelection;
  onBack: () => void;
}

/** Same timing as `.sidebar` opening and closing. */
const VIEW_SLIDE_MS = 250;

const usePhenomenonName = () => {
  const { t } = useTranslation();
  return (phenomenon: string) => riskPicturePhenomenonName(phenomenon, t);
};

const ItemBadge = ({ item }: { item: RiskPictureItem }) => (
  <span
    className={classnames(styles.mapMarkerIcon, styles.sidebarBadge)}
    data-kind={item.kind}
    data-level={item.level}
    style={{ backgroundImage: `url("${riskPictureBadgeUrl(item.kind, item.level)}")` }}
  >
    <img src={riskPicturePhenomenonIconUrl(item.phenomenon)} alt="" />
  </span>
);

/**
 * Right-hand panel over the big map. Holds two independent things: the calendar (every day
 * of the table) and one warning's details. Details win while set; closing them reveals the
 * calendar only if it was open, otherwise the panel closes.
 */
export const RiskPictureSidebar = ({
  days,
  dayIndex,
  selectedItem,
  calendarOpen,
  onCalendarOpenChange,
  onSelectDay,
  onSelectItem,
  onHoverItem,
  highlighted,
  onBack,
}: RiskPictureSidebarProps) => {
  const { t } = useTranslation();
  const open = riskPictureSidebarView(calendarOpen, Boolean(selectedItem)) !== "closed";
  // Detail stays mounted while it slides back off the calendar.
  const [detailItem, setDetailItem] = useState(selectedItem);
  // The panel's own slide already reveals the detail when it opens from the map.
  const [slideDetail, setSlideDetail] = useState(false);
  // Previous render's values, kept in state (not refs) so StrictMode/concurrent renders agree.
  const [prev, setPrev] = useState({ open, selectedId: selectedItem?.id });
  if (prev.open !== open || prev.selectedId !== selectedItem?.id) {
    setSlideDetail(
      prev.open && Boolean(selectedItem) && selectedItem?.id !== prev.selectedId,
    );
    setPrev({ open, selectedId: selectedItem?.id });
  }
  const shownDetail = selectedItem ?? detailItem;
  // Closing details with the calendar closed closes the whole panel: the detail rides out
  // with it instead of sliding back to reveal the calendar.
  const detailShown = Boolean(selectedItem) || (!open && Boolean(shownDetail));

  useEffect(() => {
    if (selectedItem) {
      setDetailItem(selectedItem);
      return;
    }
    const slideMs = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? 0
      : VIEW_SLIDE_MS;
    const timer = window.setTimeout(() => setDetailItem(undefined), slideMs);
    return () => window.clearTimeout(timer);
  }, [selectedItem]);

  // Each view's X closes only that view.
  const closeCalendar = (
    <button
      type="button"
      className={styles.sidebarIconButton}
      aria-label={t("riskPicture.sidebar.closeAria")}
      onClick={() => onCalendarOpenChange(false)}
    >
      <X size={20} />
    </button>
  );
  const closeDetails = (
    <button
      type="button"
      className={styles.sidebarIconButton}
      aria-label={t("riskPicture.sidebar.closeDetailsAria")}
      onClick={onBack}
    >
      <X size={20} />
    </button>
  );

  // Both stay mounted so open and close can animate; the panel keeps its scroll position.
  return (
    <>
      <button
        type="button"
        className={classnames(
          styles.sidebarOpenToggle,
          open && styles.sidebarOpenToggleHidden,
        )}
        aria-label={t("riskPicture.sidebar.openAria")}
        aria-hidden={open}
        tabIndex={open ? -1 : undefined}
        onClick={() => onCalendarOpenChange(true)}
      >
        <CalendarDays size={20} />
      </button>
      <aside
        className={classnames(styles.sidebar, !open && styles.sidebarClosed)}
        aria-hidden={!open}
      >
        <div className={styles.sidebarViews}>
          <div className={styles.sidebarView} aria-hidden={detailShown}>
            <DaysView
              days={days}
              dayIndex={dayIndex}
              collapse={closeCalendar}
              onSelectDay={onSelectDay}
              onSelectItem={onSelectItem}
              onHoverItem={onHoverItem}
              highlighted={highlighted}
            />
          </div>
          {shownDetail && (
            <div
              key={shownDetail.id}
              className={classnames(
                styles.sidebarDetail,
                !slideDetail && styles.sidebarDetailInstant,
                !detailShown && styles.sidebarDetailLeaving,
              )}
              aria-hidden={!detailShown}
            >
              <DetailView item={shownDetail} collapse={closeDetails} />
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

interface DaysViewProps {
  days: RiskPictureDay[];
  dayIndex: number;
  collapse: ReactNode;
  onSelectDay: (index: number) => void;
  onSelectItem: (dayIndex: number, itemId: string) => void;
  onHoverItem: (item: RiskPictureSelection | undefined) => void;
  highlighted?: RiskPictureSelection;
}

const DaysView = ({
  days,
  dayIndex,
  collapse,
  onSelectDay,
  onSelectItem,
  onHoverItem,
  highlighted,
}: DaysViewProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const phenomenonName = usePhenomenonName();
  const bodyRef = useRef<HTMLDivElement>(null);

  // Keep the selected day in view when the table changes it. Scrolls only this panel, not the page.
  useEffect(() => {
    const body = bodyRef.current;
    const day = body?.querySelector<HTMLElement>(`[data-day-index="${dayIndex}"]`);
    if (!body || !day) return;
    const top = day.offsetTop - body.offsetTop;
    if (
      top < body.scrollTop ||
      top + day.offsetHeight > body.scrollTop + body.clientHeight
    ) {
      body.scrollTo({ top, behavior: "smooth" });
    }
  }, [dayIndex]);

  // Map/timeline hover can point at a row below the fold. Scrolls only this panel.
  // Skipped while the cursor is in this list: scrolling would move rows under it.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !highlighted?.itemId || body.matches(":hover")) return;
    const row = body.querySelector<HTMLElement>(
      `[data-item-id="${CSS.escape(highlighted.itemId)}"]`,
    );
    if (!row) return;
    const top = row.offsetTop - body.offsetTop;
    if (top < body.scrollTop) {
      body.scrollTo({ top, behavior: "smooth" });
    } else if (top + row.offsetHeight > body.scrollTop + body.clientHeight) {
      body.scrollTo({
        top: top + row.offsetHeight - body.clientHeight,
        behavior: "smooth",
      });
    }
  }, [highlighted?.itemId]);

  return (
    <>
      <div className={styles.sidebarToolbar}>{collapse}</div>
      <div ref={bodyRef} className={classnames(styles.sidebarBody, styles.sidebarDays)}>
        {days.map((day, index) => {
          const title = riskPictureDayTitle(day.date, locale);

          return (
            <section key={day.date} className={styles.sidebarDay} data-day-index={index}>
              <button
                type="button"
                className={classnames(
                  styles.sidebarDayHeader,
                  index === dayIndex && styles.sidebarDayHeaderSelected,
                )}
                aria-pressed={index === dayIndex}
                onClick={() => onSelectDay(index)}
              >
                <span className={styles.sidebarDayTitle}>{title}</span>
                {index === 0 && (
                  <span className={styles.sidebarDaySubtitle}>{t("general.today")}</span>
                )}
              </button>
              {day.items.length === 0 ? (
                <p className={styles.sidebarEmpty}>{t("riskPicture.sidebar.noWarnings")}</p>
              ) : (
                day.items.map((item) => (
                  <button
                    key={item.id}
                    data-item-id={item.id}
                    type="button"
                    className={classnames(
                      styles.sidebarItem,
                      highlighted?.dayIndex === index &&
                        highlighted.itemId === item.id &&
                        styles.sidebarItemHighlighted,
                    )}
                    onMouseEnter={() => onHoverItem({ dayIndex: index, itemId: item.id })}
                    onMouseLeave={() => onHoverItem(undefined)}
                    onClick={() => {
                      onHoverItem(undefined);
                      onSelectItem(index, item.id);
                    }}
                  >
                    <span className={styles.sidebarItemLabel}>
                      {riskPictureCellLabel(item)}
                    </span>
                    <span className={styles.sidebarItemPhenomenon}>
                      {phenomenonName(item.phenomenon)}
                    </span>
                    <ItemBadge item={item} />
                  </button>
                ))
              )}
            </section>
          );
        })}
      </div>
    </>
  );
};

interface DetailViewProps {
  item: RiskPictureItem;
  collapse: ReactNode;
}

const DetailView = ({ item, collapse }: DetailViewProps) => {
  const locale = useLocale();
  const phenomenonName = usePhenomenonName();
  const period = riskPicturePeriodLabel(item.onset, item.expires);
  const texts = [
    item.headline,
    item.description,
    item.detail,
    riskPictureAreaDescription(item.areaDescription, locale),
  ].filter(Boolean);

  return (
    <>
      <div className={classnames(styles.sidebarToolbar, styles.sidebarDetailToolbar)}>
        <div className={styles.sidebarDetailHeading}>
          <ItemBadge item={item} />
          <span className={styles.sidebarDetailLabel}>
            <span className={styles.mapMarkerTitle}>{riskPictureCellLabel(item)}</span>
            <span>{phenomenonName(item.phenomenon)}</span>
          </span>
        </div>
        {collapse}
      </div>
      <div className={styles.sidebarBody}>
        {period && <p className={styles.sidebarDetailPeriod}>{period}</p>}
        {texts.map((text, index) => (
          <p key={index} className={styles.sidebarDetailText}>
            {text}
          </p>
        ))}
      </div>
    </>
  );
};
